/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './medical-history.reducer';

export const MedicalHistory = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const medicalHistoryList = useAppSelector(state => state.medicalHistory.entities);
  const loading = useAppSelector(state => state.medicalHistory.loading);
  const totalItems = useAppSelector(state => state.medicalHistory.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="medical-history-heading" data-cy="MedicalHistoryHeading">
        <Translate contentKey="tinyMedicRestApp.medicalHistory.home.title">Medical Histories</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.medicalHistory.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/medical-history/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.medicalHistory.home.createLabel">Create new Medical History</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.medicalHistory.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {medicalHistoryList && medicalHistoryList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('tmDate')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.tmDate">Tm Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('tmDate')} />
                </th>
                <th className="hand" onClick={sort('history')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.history">History</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('history')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('leverFunction')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.leverFunction">Lever Function</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('leverFunction')} />
                </th>
                <th className="hand" onClick={sort('renalFunction')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.renalFunction">Renal Function</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('renalFunction')} />
                </th>
                <th className="hand" onClick={sort('diagnosisNotes')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.diagnosisNotes">Diagnosis Notes</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('diagnosisNotes')} />
                </th>
                <th className="hand" onClick={sort('assessment')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.assessment">Assessment</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('assessment')} />
                </th>
                <th className="hand" onClick={sort('tmPlan')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.tmPlan">Tm Plan</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('tmPlan')} />
                </th>
                <th className="hand" onClick={sort('prognosis')}>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.prognosis">Prognosis</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('prognosis')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.pressure">Pressure</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.otherVitals">Other Vitals</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.consultant">Consultant</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.patient">Patient</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.prescription">Prescription</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.paediatricParams">Paediatric Params</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.patientCondition">Patient Condition</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.medicalHistory.vitals">Vitals</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {medicalHistoryList.map((medicalHistory, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/medical-history/${medicalHistory.id}`} color="link" size="sm">
                      {medicalHistory.id}
                    </Button>
                  </td>
                  <td>{medicalHistory.tmDate}</td>
                  <td>{medicalHistory.history}</td>
                  <td>{medicalHistory.active ? 'true' : 'false'}</td>
                  <td>{medicalHistory.timestampentry}</td>
                  <td>{medicalHistory.lastupdated}</td>
                  <td>{medicalHistory.cursor}</td>
                  <td>{medicalHistory.microSec}</td>
                  <td>{medicalHistory.leverFunction}</td>
                  <td>{medicalHistory.renalFunction}</td>
                  <td>{medicalHistory.diagnosisNotes}</td>
                  <td>{medicalHistory.assessment}</td>
                  <td>{medicalHistory.tmPlan}</td>
                  <td>{medicalHistory.prognosis}</td>
                  <td>
                    {medicalHistory.pressure ? (
                      <Link to={`/pressure/${medicalHistory.pressure.id}`}>{medicalHistory.pressure.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {medicalHistory.otherVitals ? (
                      <Link to={`/other-vitals/${medicalHistory.otherVitals.id}`}>{medicalHistory.otherVitals.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {medicalHistory.consultant ? (
                      <Link to={`/doctor/${medicalHistory.consultant.id}`}>{medicalHistory.consultant.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {medicalHistory.patient ? <Link to={`/gluco-user/${medicalHistory.patient.id}`}>{medicalHistory.patient.id}</Link> : ''}
                  </td>
                  <td>
                    {medicalHistory.prescription ? (
                      <Link to={`/prescription/${medicalHistory.prescription.id}`}>{medicalHistory.prescription.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {medicalHistory.paediatricParams ? (
                      <Link to={`/paediatric-params/${medicalHistory.paediatricParams.id}`}>{medicalHistory.paediatricParams.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {medicalHistory.patientCondition ? (
                      <Link to={`/patient-condition-check-up/${medicalHistory.patientCondition.id}`}>
                        {medicalHistory.patientCondition.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>{medicalHistory.vitals ? <Link to={`/vitals/${medicalHistory.vitals.id}`}>{medicalHistory.vitals.id}</Link> : ''}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/medical-history/${medicalHistory.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/medical-history/${medicalHistory.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/medical-history/${medicalHistory.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.medicalHistory.home.notFound">No Medical Histories found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={medicalHistoryList && medicalHistoryList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default MedicalHistory;
